import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/Dialog";
import { Button } from "src/components/ui/Button";
import { Loader2, ArrowRight, Clock } from "lucide-react";
import EmailEntryForm from "src/components/auth/EmailEntryForm";
import { ROUTES, getRouteForUser } from "src/routes/RouteConfig";
import {
  shouldShowTutorApplicationOptions,
  needsCompleteSignup,
  needsContinueToOnboarding,
} from "src/utils/tutorStateUtils";
import { useUserStatusCheck } from "src/hooks/useUserStatusCheck";
import { config } from "src/config/environment";
import HeroPair from "./HeroPair";

const Hero = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [modalState, setModalState] = useState<"email-entry" | "processing" | "result">("email-entry");

  const {
    checkUserStatus,
    isOnboarded,
    userStatus,
    reset,
  } = useUserStatusCheck();

  useEffect(() => {
    if (!isModalOpen) {
      setTimeout(() => {
        setModalState("email-entry");
        reset();
      }, 300);
    }
  }, [isModalOpen]);

  const handleEmailSubmitted = async (email: string) => {
    setSubmittedEmail(email);
    setModalState("processing");
    await checkUserStatus(email);
    setModalState("result");
  };

  const handleFindTutorClick = () => navigate(ROUTES.BOOK_DEMO.path);
  const handleBecomeTutorClick = () => {
    setModalState("email-entry");
    setIsModalOpen(true);
  };

  const renderModalContent = () => {
    switch (modalState) {
      case "processing":
        return (
          <>
            <DialogHeader>
              <DialogTitle>Checking your email</DialogTitle>
              <DialogDescription>
                Please wait while we check if you've used this email before.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
            </div>
          </>
        );
      case "result":
        if (needsContinueToOnboarding(userStatus, "tutor", isOnboarded)) {
          return (
            <>
              <DialogHeader>
                <DialogTitle>Welcome back!</DialogTitle>
                <DialogDescription>
                  Complete your tutor onboarding to continue.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => navigate(getRouteForUser("tutor", isOnboarded, userStatus, "become-tutor"))}>
                  Continue to onboarding
                </Button>
              </DialogFooter>
            </>
          );
        }
        if (needsCompleteSignup(userStatus)) {
          return (
            <>
              <DialogHeader>
                <DialogTitle>Complete signup</DialogTitle>
                <DialogDescription>Set up your password to continue.</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => navigate(ROUTES.REGISTER.path, {
                  state: { email: submittedEmail, source: "guest-upgrade" }
                })}>
                  Complete sign up
                </Button>
              </DialogFooter>
            </>
          );
        }
        if (shouldShowTutorApplicationOptions(userStatus)) {
          return (
            <>
              <DialogHeader>
                <DialogTitle>New here?</DialogTitle>
                <DialogDescription>Create your account to proceed.</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => navigate(ROUTES.REGISTER.path, {
                  state: { email: submittedEmail, source: "become-tutor" },
                })}>
                  Create an account
                </Button>
              </DialogFooter>
            </>
          );
        }
        return (
          <>
            <DialogHeader>
              <DialogTitle>Account Found</DialogTitle>
              <DialogDescription>You cannot proceed. Contact support.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => setIsModalOpen(false)}>Close</Button>
            </DialogFooter>
          </>
        );
      case "email-entry":
      default:
        return (
          <>
            <DialogHeader>
              <DialogTitle>Become a Tutor</DialogTitle>
              <DialogDescription>
                Enter your email to check account status or sign up.
              </DialogDescription>
            </DialogHeader>
            <EmailEntryForm onEmailSubmitted={handleEmailSubmitted} />
          </>
        );
    }
  };

  // Create buttons component
  const buttons = (
    <div className="flex flex-col sm:flex-row justify-center items-center gap-4 px-4 max-md:w-full max-md:flex-row max-md:gap-3 max-md:px-5">
      <button
        onClick={handleFindTutorClick}
        className="w-full sm:w-48 px-6 py-3 rounded-sm text-white text-lg font-medium transition-all duration-200 hover:shadow-lg hover:scale-105 max-md:min-w-0 max-md:flex-1 max-md:px-3 max-md:py-3 max-md:text-base"
        style={{ backgroundColor: "#ff7f52" }}
      >
        Book a Demo
      </button>
      <button
        onClick={() => navigate(ROUTES.REGISTER.path)}
        className="w-full sm:w-48 px-6 py-3 rounded-sm text-lg font-medium transition-all duration-200 hover:shadow-lg hover:scale-105 bg-white max-md:min-w-0 max-md:flex-1 max-md:px-3 max-md:py-3 max-md:text-base"
        style={{ border: "1.5px solid #ff7f52", color: "#ff7f52" }}
      >
        Get Started
      </button>
    </div>
  );

  return (
    <section className="relative h-full max-md:flex max-md:flex-col">
      <HeroPair buttons={buttons} />

      {/* Text overlay - centered in left card */}
      <div className="absolute inset-0 z-10 flex flex-row mx-2 sm:mx-4 md:mx-9 pb-12 max-md:static max-md:order-1 max-md:mx-0 max-md:block max-md:pb-0">
        <div className="flex w-1/2 items-center justify-center px-2 max-md:w-full max-md:px-5 max-md:pt-8">
          <div className="max-w-md w-full">
            <h1
              className="relative m-0 mb-5 max-w-md text-left text-3xl font-extrabold leading-[0.98] tracking-[-0.035em] sm:text-4xl md:text-[2.75rem] lg:text-5xl"
              style={{ fontFamily: "Roboto, sans-serif", color: "#3e1168" }}
            >
              <span className="relative inline-block">
                <svg
                  viewBox="0 0 140 60"
                  className="absolute inset-0 -z-10"
                  preserveAspectRatio="none"
                  style={{ 
                    width: "108%", 
                    height: "120%",
                    transform: "translateY(-15%) translateX(-4%) translateY(4px)"
                  }}
                >
                  <defs>
                    <linearGradient id="audioGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgba(255, 215, 0, 0.5)" />
                      <stop offset="20%" stopColor="rgba(255, 193, 7, 0.8)" />
                      <stop offset="40%" stopColor="rgba(255, 215, 0, 0.9)" />
                      <stop offset="60%" stopColor="rgba(255, 193, 7, 0.9)" />
                      <stop offset="80%" stopColor="rgba(255, 215, 0, 0.8)" />
                      <stop offset="100%" stopColor="rgba(255, 193, 7, 0.5)" />
                    </linearGradient>
                    <linearGradient id="audioGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgba(255, 215, 0, 0.6)" />
                      <stop offset="25%" stopColor="rgba(255, 193, 7, 0.8)" />
                      <stop offset="50%" stopColor="rgba(255, 215, 0, 0.9)" />
                      <stop offset="75%" stopColor="rgba(255, 193, 7, 0.8)" />
                      <stop offset="100%" stopColor="rgba(255, 215, 0, 0.6)" />
                    </linearGradient>
                    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                      <feMerge> 
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  
                  {/* Audio waveform bars - thick overlapping brush effect */}
                  <rect x="2" y="28" width="8" height="6" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="6" y="24" width="8" height="14" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="10" y="20" width="8" height="22" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="14" y="16" width="8" height="30" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="18" y="22" width="8" height="18" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="22" y="18" width="8" height="26" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="26" y="14" width="8" height="34" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="30" y="26" width="8" height="10" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="34" y="12" width="8" height="38" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="38" y="20" width="8" height="22" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="42" y="24" width="8" height="14" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="46" y="16" width="8" height="30" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="50" y="22" width="8" height="18" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="54" y="10" width="8" height="42" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="58" y="18" width="8" height="26" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="62" y="14" width="8" height="34" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="66" y="26" width="8" height="10" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="70" y="8" width="8" height="46" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="74" y="20" width="8" height="22" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="78" y="16" width="8" height="30" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="82" y="22" width="8" height="18" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="86" y="12" width="8" height="38" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="90" y="18" width="8" height="26" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="94" y="24" width="8" height="14" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="98" y="14" width="8" height="34" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="102" y="20" width="8" height="22" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="106" y="26" width="8" height="10" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="110" y="16" width="8" height="30" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="114" y="22" width="8" height="18" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="118" y="28" width="8" height="6" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="122" y="24" width="8" height="14" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  <rect x="126" y="30" width="8" height="2" fill="url(#audioGradient2)" filter="url(#glow)" rx="3"/>
                  <rect x="130" y="26" width="8" height="10" fill="url(#audioGradient)" filter="url(#glow)" rx="3"/>
                  
                  {/* Baseline - horizontal line representing the audio baseline */}
                  <line x1="0" y1="32" x2="140" y2="32" stroke="rgba(255, 215, 0, 0.3)" strokeWidth="1"/>
                </svg>
                Reinforce
              </span>{" "}
              Your <br /> Learning Journey
            </h1>
            <p
              className="m-0 max-w-md text-left text-[1.05rem] leading-relaxed sm:text-lg md:text-xl"
              style={{ fontFamily: "Segoe UI, sans-serif" }}
            >
              Experience the power of reinforcement-based learning with AI support and human expertise. Master subjects
              faster, retain knowledge longer.
            </p>

            {/* Diagnostic Assessment Entry Point (Option 1: Interactive Micro-Banner) */}
            {config.features.diagnosticAssessment && (
              <Link
                to={ROUTES.DIAGNOSTIC_ASSESSMENT.path}
                className="group mt-5 sm:mt-6 block w-full max-w-md rounded-xl border border-[#fed7c7] bg-white/95 p-3.5 sm:p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[#ff7f52] hover:shadow-md"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center rounded-full bg-[#ffede6] px-2.5 py-0.5 text-xs font-semibold text-[#e05828]">
                        Free Assessment
                      </span>
                      <span className="flex items-center gap-1 text-xs text-purple-900/60 font-medium">
                        <Clock className="h-3 w-3 text-purple-900/50" />
                        15 min
                      </span>
                    </div>
                    <h3
                      className="mt-1.5 text-[0.98rem] sm:text-base font-bold leading-snug text-[#3e1168] transition-colors group-hover:text-[#ff7f52]"
                      style={{ fontFamily: "Segoe UI, sans-serif" }}
                    >
                      Pinpoint learning gaps. Strengthen core skills.
                    </h3>
                  </div>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#f5f3ff] text-[#3e1168] transition-all duration-200 group-hover:bg-[#ff7f52] group-hover:text-white">
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </div>
                </div>
              </Link>
            )}
            <div className="mt-6 hidden max-md:block">{buttons}</div>
          </div>
        </div>
        <div className="w-1/2 max-md:hidden" />
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          {renderModalContent()}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Hero;
